import React, { FC, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios, { AxiosResponse } from 'axios'
import * as yup from 'yup'
import { getAuthClient, api, routes } from 'config'
import {
  constants,
  errorMsg,
  processErrorMsg,
  removeFocusFromField,
  resetAndExpiredPasswordSchema,
  storage,
} from 'utils'
import { BaseLayout, Loader } from 'components'
import { PasswordExpiredForm } from './components'
import { useOktaAuth } from '@okta/okta-react'

const PasswordExpired: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [transaction, setTransaction] = useState<any | {}>({})
  const [serviceError, setServiceError] = useState('')
  const [showLoader, setShowLoader] = useState(false)

  const { oktaAuth } = useOktaAuth()

  useEffect(() => {
    let authClient = getAuthClient()

    if (authClient.tx.exists()) {
      setShowLoader(true)

      authClient.tx
        .resume()
        .then((response: any) => {
          storage.setUser(response?.user?.profile?.login)

          setShowLoader(false)
          setTransaction(response)
        })
        .catch((e: any) =>
          processError(e, 'Password Expired', 'Resuming transaction'),
        )
    } else {
      navigate(routes.login)
    }
  }, [navigate])

  const processSuccess = (response: any) => {
    setShowLoader(false)
    setTransaction(response)
  }

  const processError = (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    removeFocusFromField()

    setSubmitting(true)

    setShowLoader(true)
    setServiceError('')
    let transactionResponse: any

    if (
      transaction.status ===
        constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED ||
      transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
    ) {
      try {
        transactionResponse = await transaction.changePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        })
      } catch (e) {
        setShowLoader(false)
        setSubmitting(false)
        processError(e, 'Password Expired', 'Resetting Okta password')
        return
      }

      try {
        const response: AxiosResponse = await axios.post(api.savePassword(), {
          userId: transaction.user.profile.login,
          newPassword: values.newPassword,
          passwordChangeType: constants.PASSWORD_CHANGE_TYPE.FORGOT_PASSWORD,
        })

        if (response.status === 200) {
          setSubmitting(false)
          processSuccess(transactionResponse)
        } else {
          throw response
        }
      } catch (e) {
        setSubmitting(false)
        processErrorMsg(e, 'Password Expired', 'Saving encrypted password')
        processSuccess(transactionResponse)
      }

      /**
       * Cancelling the transaction and redirecting to login
       */
      transaction
        .cancel()
        .then(() => {
          setShowLoader(false)
          navigate(routes.login, {
            state: {
              message:
                'Your password has been successfully changed. Please log in below.',
            },
          })
          return
        })
        .catch((e: any) =>
          processError(
            e,
            'Password Expired',
            'Password reset successfully and redirecting to login page',
          ),
        )
    }
  }

  const handleCancel = (e: any) => {
    e.preventDefault()

    setServiceError('')
    setShowLoader(true)

    transaction
      .cancel()
      .then(() => {
        setShowLoader(false)
        navigate(routes.login)
      })
      .catch((error: any) =>
        processError(
          error,
          'Password Expired ',
          'Cancelling resetting of password and redirecting to login page',
        ),
      )
  }

  const handleSkip = (e: any) => {
    e.preventDefault()

    setServiceError('')
    setShowLoader(true)

    transaction
      .skip()
      .then((response: any) => {
        setShowLoader(false)
        oktaAuth.signInWithRedirect({ sessionToken: response.sessionToken })
        return null
      })
      .catch((error: any) =>
        processError(
          error,
          'Password Expiring Warning',
          'Cancelling skipping of expiring password warning and redirecting to login page',
        ),
      )
  }

  if (transaction.sessionToken) {
    return null
  }

  let initialValues: any = {}
  let validationSchema: any = yup.object().shape({})

  if (
    transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED
  ) {
    const state = location.state as {
      loginPassword: string
    }

    initialValues = {
      oldPassword: state?.loginPassword,
      newPassword: '',
      repeatPassword: '',
    }

    validationSchema = yup.object().shape({
      oldPassword: yup
        .string()
        .required(errorMsg.requiredField('Old password')),
      ...resetAndExpiredPasswordSchema(transaction.policy.complexity.minLength),
    })
  }

  return (
    <BaseLayout>
      <Loader visible={showLoader} />
      <PasswordExpiredForm
        isModuleInitialized={Boolean(transaction.status)}
        passwordPolicy={transaction.policy}
        initialValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        handleSkip={handleSkip}
        isPasswordExpiring={
          transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
        }
        serviceError={serviceError}
      />
    </BaseLayout>
  )
}

export default PasswordExpired
